import {useEffect, useState} from 'react'
import axios from 'axios'
import {Alert, Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, Radio, RadioGroup, TextField, Typography} from '@mui/material'

import {Button, Modal} from '@origamirisk/origami-storybook'
import StateSelector from './StateSelector'
import {AccountMeta} from '../../utils/types'

export interface BulkRatingCreatedResponse {
    requestId: string
    totalCount: number
}

interface CreateBulkRatingModalProps {
    open: boolean
    onClose: () => void
    onCreated?: (response: BulkRatingCreatedResponse) => void
    accountName: string
}

function CreateBulkRatingModal({open, onClose, onCreated, accountName}: CreateBulkRatingModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedStates, setSelectedStates] = useState<string[]>([])
    const [proposalIds, setProposalIds] = useState('')
    const [excludeIds, setExcludeIds] = useState('')
    const [inputMode, setInputMode] = useState<'states' | 'proposals'>('states')
    const [includeExecutionTree, setIncludeExecutionTree] = useState(false)
    const [includeLogs, setIncludeLogs] = useState(false)
    const [isSecondPass, setIsSecondPass] = useState(true)
    const [includeWorksheet, setIncludeWorksheet] = useState(false)
    const [includeTailEquations, setIncludeTailEquations] = useState(false)
    const [includeDataComparison, setIncludeDataComparison] = useState(false)
    const [accountMeta, setAccountMeta] = useState<AccountMeta | null>(null)
    const [metaLoading, setMetaLoading] = useState(false)
    const [selectedPolicySet, setSelectedPolicySet] = useState<string | null>(null)
    const [selectedCarriers, setSelectedCarriers] = useState<string[]>([])
    const [includeEndorsements, setIncludeEndorsements] = useState(false)
    const [noProposalsMatchedVisible, setNoProposalsMatchedVisible] = useState(false)

    useEffect(() => {
        setNoProposalsMatchedVisible(false)
    }, [inputMode, selectedStates, proposalIds, excludeIds, selectedPolicySet, selectedCarriers])

    useEffect(() => {
        const fetchAccountMeta = async () => {
            if (!accountName) return

            setMetaLoading(true)
            try {
                const {data} = await axios.get(`/api/admin/account/meta`)
                setAccountMeta(data)
            } catch (err) {
                console.error('Failed to fetch account metadata:', err)
            } finally {
                setMetaLoading(false)
            }
        }

        fetchAccountMeta()
    }, [accountName])

    const isFormValid = () => {
        if (!accountName) return false
        if (!selectedPolicySet) return false
        if (inputMode === 'states') return selectedStates.length > 0
        if (inputMode === 'proposals') {
            const ids = proposalIds.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id))
            return ids.length > 0
        }
        return false
    }

    const parseIds = (input: string): number[] => {
        return input
            .split(',')
            .map(id => parseInt(id.trim(), 10))
            .filter(id => !isNaN(id))
    }

    const getSelectedPolicySetRatingMode = (): string | null => {
        if (!selectedPolicySet || !accountMeta) return null
        const policySet = accountMeta.policySets.find(ps => ps.id === selectedPolicySet)
        return policySet?.ratingMode || null
    }

    const shouldShowIncludeEndorsements = (): boolean => {
        const ratingMode = getSelectedPolicySetRatingMode()
        if (!ratingMode) return false
        return ratingMode.includes('AL') || ratingMode.includes('HA')
    }

    const handleSubmit = async () => {
        if (!isFormValid()) return

        setIsSubmitting(true)
        try {
            const payload: any = {
                policySetId: Number(selectedPolicySet),
                lambdaOptions: {
                    includeExecutionTree,
                    includeLogs,
                    isSecondPass,
                    includeWorksheet,
                    includeTailEquations,
                    includeDataComparison
                }
            }

            if (inputMode === 'states') {
                payload.states = selectedStates
                const excludeIdArray = parseIds(excludeIds)
                if (excludeIdArray.length > 0) {
                    payload.excludeIds = excludeIdArray
                }
            } else {
                payload.proposalIds = parseIds(proposalIds)
            }

            if (selectedCarriers.length > 0) {
                payload.carrierIds = selectedCarriers.map(Number)
            }

            if (includeEndorsements && shouldShowIncludeEndorsements()) {
                payload.includeEndorsements = true
            }

            console.log('Submitting bulk rating payload:', payload)
            const response = await axios.post<BulkRatingCreatedResponse>('/api/bulk-rating', payload)
            const {totalCount} = response.data
            if (totalCount === 0) {
                setNoProposalsMatchedVisible(true)
                return
            }
            setNoProposalsMatchedVisible(false)
            onCreated?.(response.data)
            onClose()
        } catch (error) {
            console.error('Failed to create bulk rating:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="md"
            title="Create Bulk Rating"
            actions={
                <Box sx={{display: 'flex', gap: 1}}>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isFormValid()}
                    >
                        {isSubmitting ? (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <CircularProgress size={16} sx={{color: 'white'}}/>
                                <span>Creating...</span>
                            </Box>
                        ) : (
                            'Create'
                        )}
                    </Button>
                </Box>
            }
        >
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                {noProposalsMatchedVisible && (
                    <Alert severity="warning" onClose={() => setNoProposalsMatchedVisible(false)}>
                        No proposals matched your selection. Change states, proposal IDs, rating programs, or carriers and
                        try again.
                    </Alert>
                )}

                <Typography variant="body2" sx={{fontWeight: 500, mb: 1}}>
                    Select Rating Program
                </Typography>
                {accountMeta && (
                    <Autocomplete
                        size="small"
                        loading={metaLoading}
                        options={[...accountMeta.policySets].sort((a, b) => a.name.localeCompare(b.name))}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedPolicySet ? accountMeta.policySets.find(ps => ps.id === selectedPolicySet) || null : null}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                setSelectedPolicySet(newValue.id)
                            } else {
                                setSelectedPolicySet(null)
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Rating Program"
                                required
                                error={!selectedPolicySet}
                                helperText={!selectedPolicySet ? 'Rating Program is required' : ''}
                            />
                        )}
                    />
                )}

                <Box>
                    <Typography variant="body2" sx={{fontWeight: 500, mb: 1}}>
                        Select Proposals
                    </Typography>
                    <RadioGroup
                        value={inputMode}
                        onChange={(e) => setInputMode(e.target.value as 'states' | 'proposals')}
                        row
                    >
                        <FormControlLabel
                            value="states"
                            control={<Radio size="small"/>}
                            label="By States"
                        />
                        <FormControlLabel
                            value="proposals"
                            control={<Radio size="small"/>}
                            label="By Proposal IDs"
                        />
                    </RadioGroup>
                </Box>

                {inputMode === 'proposals' && (
                    <TextField
                        label="Proposal IDs"
                        value={proposalIds}
                        onChange={(e) => setProposalIds(e.target.value)}
                        fullWidth
                        required
                        size="small"
                        placeholder="e.g., 1, 2, 3"
                        helperText="Enter proposal IDs separated by commas"
                    />
                )}

                {inputMode === 'states' && (
                    <>
                        <StateSelector
                            accountName={accountName}
                            selectedStates={selectedStates}
                            onStatesChange={setSelectedStates}
                        />
                        <TextField
                            label="Exclude Proposal IDs (optional)"
                            value={excludeIds}
                            onChange={(e) => setExcludeIds(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g., 1, 2, 3"
                            helperText="Enter proposal IDs to exclude, separated by commas"
                        />
                    </>
                )}

                {/* Include Endorsements - only show for splitless rating modes */}
                {shouldShowIncludeEndorsements() && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeEndorsements}
                                onChange={(e) => setIncludeEndorsements(e.target.checked)}
                                disabled={isSubmitting}
                                size="small"
                            />
                        }
                        label="Include Endorsements"
                    />
                )}

                {accountMeta && (
                    <Autocomplete
                        multiple
                        size="small"
                        loading={metaLoading}
                        options={Object.keys(accountMeta.carrierMap).sort((a, b) =>
                            accountMeta.carrierMap[a].localeCompare(accountMeta.carrierMap[b]))}
                        value={selectedCarriers}
                        onChange={(_, newValue) => {
                            setSelectedCarriers(newValue)
                        }}
                        getOptionLabel={(option) => accountMeta.carrierMap[option]}
                        renderInput={(params) => (
                            <TextField {...params} label="Carrier" />
                        )}
                        disableCloseOnSelect
                    />
                )}

               <Box>
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={includeExecutionTree}
                               onChange={(e) => setIncludeExecutionTree(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Execution Tree"
                   />
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={includeLogs}
                               onChange={(e) => setIncludeLogs(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Logs"
                   />
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={isSecondPass}
                               onChange={(e) => setIsSecondPass(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Second Pass"
                   />
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={includeWorksheet}
                               onChange={(e) => setIncludeWorksheet(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Worksheet"
                   />
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={includeTailEquations}
                               onChange={(e) => setIncludeTailEquations(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Tail Equations"
                   />
                   <FormControlLabel
                       control={
                           <Checkbox
                               checked={includeDataComparison}
                               onChange={(e) => setIncludeDataComparison(e.target.checked)}
                               disabled={isSubmitting}
                           />
                       }
                       label="Include Data Comparison"
                   />
               </Box>
            </Box>
        </Modal>
    )
}

export default CreateBulkRatingModal
